import type { ColorSpec } from './types';

/**
 * Yellow color spec — uses the `quarter-spanish-white` Tailwind palette.
 *
 * Differs from blue/red/green/purple because yellow's palette is bright:
 * filled buttons use shade-200 (not 600), text is dark instead of white, and
 * some hover states stay lighter. This shape is identical to other colors —
 * the *values* just lean lighter.
 */
export const yellow: ColorSpec = {
  filled: {
    border: 'border-quarter-spanish-white-300 dark:border-quarter-spanish-white-600',
    bg: 'bg-quarter-spanish-white-200 text-quarter-spanish-white-800 dark:bg-quarter-spanish-white-800 dark:text-quarter-spanish-white-200',
    bgActive:
      'bg-quarter-spanish-white-400 text-quarter-spanish-white-900 dark:bg-quarter-spanish-white-500 dark:text-charade-950',
    hover:
      'hover:bg-quarter-spanish-white-300 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-400 active:text-quarter-spanish-white-900',
    hoverDark:
      'dark:hover:bg-quarter-spanish-white-700 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-600',
  },
  outline: {
    border: 'border-quarter-spanish-white-400 dark:border-quarter-spanish-white-600',
    bg: 'bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900 text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200',
    bgActive: 'bg-quarter-spanish-white-500 dark:bg-quarter-spanish-white-400 text-white dark:text-charade-950',
    hover:
      'hover:bg-quarter-spanish-white-100 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-200 active:text-quarter-spanish-white-900',
    hoverDark:
      'dark:hover:bg-quarter-spanish-white-800 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-700 dark:active:text-quarter-spanish-white-100',
    hoverWhenActive: 'hover:bg-quarter-spanish-white-600 dark:hover:bg-quarter-spanish-white-500',
  },
  soft: {
    border: 'border-quarter-spanish-white-100 dark:border-quarter-spanish-white-800',
    bg: 'bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900 text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200 font-normal',
    bgActive: 'bg-quarter-spanish-white-500 dark:bg-quarter-spanish-white-400 text-white dark:text-charade-950',
    hover:
      'hover:bg-quarter-spanish-white-100 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-200 active:text-quarter-spanish-white-950',
    hoverDark:
      'dark:hover:bg-quarter-spanish-white-800 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-700 dark:active:text-quarter-spanish-white-100',
    hoverWhenActive: 'hover:bg-quarter-spanish-white-600 dark:hover:bg-quarter-spanish-white-500',
  },
  textFilled: {
    text: 'text-quarter-spanish-white-800 dark:text-quarter-spanish-white-300',
    activeBg: 'bg-quarter-spanish-white-300 text-quarter-spanish-white-900',
    hover: 'hover:bg-quarter-spanish-white-200 active:bg-quarter-spanish-white-300',
    // Yellow has no dark text-mode hover override today — keep empty.
    hoverDark: '',
  },
  textSoft: {
    text: 'text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200 font-normal',
    activeBg:
      'bg-quarter-spanish-white-500 text-white dark:bg-quarter-spanish-white-400 dark:text-quarter-spanish-white-950',
    hover:
      'hover:bg-quarter-spanish-white-100 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-200',
    hoverDark:
      'dark:hover:bg-quarter-spanish-white-900 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-800',
  },
  tooltip:
    'bg-quarter-spanish-white-200 dark:bg-quarter-spanish-white-700 text-quarter-spanish-white-800 dark:text-quarter-spanish-white-100',
  badge: {
    filled: 'bg-quarter-spanish-white-600 dark:bg-quarter-spanish-white-700 text-white',
    soft: 'bg-quarter-spanish-white-400 dark:bg-quarter-spanish-white-500 text-white',
  },
  softIcon: 'text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200',
  underline: {
    filled:
      '[&_u]:text-quarter-spanish-white-900 dark:[&_u]:text-quarter-spanish-white-200 [&_u]:decoration-quarter-spanish-white-700 dark:[&_u]:decoration-quarter-spanish-white-300',
    outline:
      '[&_u]:text-quarter-spanish-white-900 dark:[&_u]:text-quarter-spanish-white-300 [&_u]:decoration-quarter-spanish-white-700 dark:[&_u]:decoration-quarter-spanish-white-400',
    soft: '[&_u]:text-quarter-spanish-white-900 dark:[&_u]:text-quarter-spanish-white-300 [&_u]:decoration-quarter-spanish-white-400 dark:[&_u]:decoration-quarter-spanish-white-400',
  },
  input: {
    filled: {
      border: 'border-quarter-spanish-white-400 dark:border-quarter-spanish-white-500',
      bg: 'bg-quarter-spanish-white-200 dark:bg-quarter-spanish-white-700',
      focusBg: 'focus-within:bg-quarter-spanish-white-300 dark:focus-within:bg-quarter-spanish-white-600',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-quarter-spanish-white-400 dark:focus-within:outline-quarter-spanish-white-500',
      hoverActive:
        'hover:bg-quarter-spanish-white-300 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-400 dark:hover:bg-quarter-spanish-white-600 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-500',
      readOnlyBg: 'bg-quarter-spanish-white-200 dark:bg-quarter-spanish-white-800',
      text: 'text-quarter-spanish-white-900 dark:text-quarter-spanish-white-100',
    },
    outline: {
      border: 'border-quarter-spanish-white-400 dark:border-quarter-spanish-white-600',
      bg: 'bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900',
      focusBg: 'focus-within:bg-quarter-spanish-white-100 dark:focus-within:bg-quarter-spanish-white-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-quarter-spanish-white-400 dark:focus-within:outline-quarter-spanish-white-600',
      hoverActive:
        'hover:bg-quarter-spanish-white-100 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-200 active:text-quarter-spanish-white-900 dark:hover:bg-quarter-spanish-white-800 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-700 dark:active:text-quarter-spanish-white-100',
      readOnlyBg: 'bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900',
      text: 'text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200',
    },
    soft: {
      border: 'border-quarter-spanish-white-100 dark:border-quarter-spanish-white-800',
      bg: 'bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900',
      focusBg: 'focus-within:bg-quarter-spanish-white-100 dark:focus-within:bg-quarter-spanish-white-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-quarter-spanish-white-100 dark:focus-within:outline-quarter-spanish-white-800',
      hoverActive:
        'hover:bg-quarter-spanish-white-100 hover:text-quarter-spanish-white-900 active:bg-quarter-spanish-white-200 active:text-quarter-spanish-white-950 dark:hover:bg-quarter-spanish-white-800 dark:hover:text-quarter-spanish-white-100 dark:active:bg-quarter-spanish-white-700 dark:active:text-quarter-spanish-white-100',
      readOnlyBg: 'bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900',
      text: 'text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200',
    },
  },
  inputMuted: {
    filled: 'text-quarter-spanish-white-800',
    outline: 'text-quarter-spanish-white-400 dark:text-quarter-spanish-white-600',
    soft: 'text-quarter-spanish-white-400 dark:text-quarter-spanish-white-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-quarter-spanish-white-800',
    outline: 'placeholder:text-quarter-spanish-white-400 dark:placeholder:text-quarter-spanish-white-600',
    soft: 'placeholder:text-quarter-spanish-white-400 dark:placeholder:text-quarter-spanish-white-600',
  },
  group: {
    filled: 'border-quarter-spanish-white-300 dark:border-charade-600 bg-quarter-spanish-white-100 dark:bg-charade-800',
    outline:
      'border-quarter-spanish-white-400 dark:border-quarter-spanish-white-600 bg-quarter-spanish-white-50 dark:bg-charade-900',
    soft: 'border-quarter-spanish-white-200 dark:border-charade-700 bg-quarter-spanish-white-100 dark:bg-charade-800',
  },
  groupDivider: {
    filled: 'bg-quarter-spanish-white-300 dark:bg-charade-600',
    outline: 'bg-quarter-spanish-white-300 dark:bg-quarter-spanish-white-700',
    soft: 'bg-quarter-spanish-white-200 dark:bg-charade-600',
  },
  segment: {
    filled: 'border-quarter-spanish-white-300 dark:border-charade-600 bg-quarter-spanish-white-100 dark:bg-charade-800',
    outline:
      'border-quarter-spanish-white-400 dark:border-quarter-spanish-white-600 bg-quarter-spanish-white-50 dark:bg-charade-900',
  },
  segmentDivider: {
    filled: 'bg-quarter-spanish-white-300 dark:bg-charade-600',
    outline: 'bg-quarter-spanish-white-300 dark:bg-quarter-spanish-white-700',
  },
  popoverHighlight: 'bg-quarter-spanish-white-300 text-quarter-spanish-white-900',
  popoverText: 'text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200',
  popoverBorder: 'border-quarter-spanish-white-200 dark:border-quarter-spanish-white-700',
};
